import React from 'react';
import { createBrowserRouter, Navigate, RouteObject, useLocation } from 'react-router-dom';
import { ShareDownloadPage } from './pages/ShareDownloadPage';
import { AdminGoogleCallbackRoute } from './domains/Approval/pages/AdminGoogleCallbackRoute';
import { ClientMagicLoginRoute } from './domains/Approval/ClientMagicLoginRoute';
import { LinkLoginRoute } from './domains/Approval/pages/LinkLoginRoute';
import { createPortalRoutes } from './domains/Approval/routes/portalRouter';
import { EditorView } from './domains/Editor/EditorView';
import { EditorProfile } from './domains/Editor/EditorProfile';
import { EditorImageLibrary } from './domains/Editor/components/EditorImageLibrary';
import { EditorVideoLibrary } from './domains/Editor/components/EditorVideoLibrary';
import { EditorClientProvider } from './domains/Editor/contexts/EditorClientContext';
import { LegacyBriefLinkNotice } from './domains/Briefs/components/LegacyBriefLinkNotice';
import { CreatorApp } from './domains/Creator/CreatorApp';
import { MainApp } from './MainApp';
import { AdminLayout } from './layouts/AdminLayout';
import { LeadsView } from './domains/Lead/LeadsView';
import { PlacesView } from './domains/Lead/PlacesView';
import { AgencyFinancePage } from './domains/Finance/agency/AgencyFinancePage';
import { ClientsView } from './domains/Client/ClientsView';
import { SetupView } from './domains/AgencySettings/SetupView';
import { TasksView } from './domains/Tasks/TasksView';

/** Serves /login and the legacy /creator/login: both land on the unified
 * portal login (DD-026, spec 2026-10-15), preserving any ?redirect= deep link. */
const PortalLoginAlias: React.FC = () => {
  const { search } = useLocation();
  return <Navigate to={`/portal/login${search}`} replace />;
};

// Creator routes (on-site photographer today; video editor later).
// Independent top-level tree mirroring editorRoutes. Auth is enforced
// inside CreatorApp via the portal JWT in localStorage.
const creatorRoutes: RouteObject[] = [
  {
    path: '/creator/:clientId',
    element: <CreatorApp />,
  },
  {
    path: '/creator/:clientId/briefs/:briefId',
    element: <CreatorApp />,
  },
];

// Editor routes
const editorRoutes: RouteObject[] = [
  {
    path: '/editor/:clientId',
    element: (
      <EditorClientProvider>
        <EditorView />
      </EditorClientProvider>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="library/images" replace />,
      },
      {
        path: 'profile',
        element: <EditorProfile />,
      },
      {
        path: 'library/images',
        element: <EditorImageLibrary />,
      },
      {
        path: 'library/videos',
        element: <EditorVideoLibrary />,
      },
    ],
  },
];

export const router = createBrowserRouter([
  {
    // Main routes with shared AdminLayout (the agency nav tabs)
    element: <AdminLayout />,
    children: [
      {
        // Tasks is the admin home (DD-049): the root, login and the
        // workspace "Dashboard" link all land on it.
        path: '/',
        element: <Navigate to="/tasks" replace />,
      },
      {
        path: '/tasks',
        element: <TasksView />,
      },
      {
        path: '/clients',
        element: <ClientsView />,
      },
      {
        path: '/leads',
        element: <LeadsView />,
      },
      {
        path: '/finance',
        element: <AgencyFinancePage />,
      },
      {
        path: '/places',
        element: <PlacesView />,
      },
      {
        path: '/setup',
        element: <SetupView />,
      },
    ],
  },
  {
    // Client workspace - the main app for working on a specific client
    // Uses /* to enable nested Routes inside MainApp
    path: '/app/:clientId/*',
    element: <MainApp />,
  },
  {
    path: '/share/:token',
    element: <ShareDownloadPage />,
  },
  {
    // Retired photographer route. Keeps old bookmarks from 404ing and
    // nudges users to request a new magic link.
    path: '/brief/*',
    element: <LegacyBriefLinkNotice />,
  },
  ...createPortalRoutes(),
  ...editorRoutes,
  ...creatorRoutes,
  {
    path: '/login',
    element: <PortalLoginAlias />,
  },
  {
    // Single self-serve login for every portal role (DD-026): the backend
    // resolves role per email row, then the flow funnels role -> client.
    path: '/portal/login',
    element: <ClientMagicLoginRoute />,
  },
  {
    // Emailed one-time login link (DD-026): one link per email, the app
    // funnels role -> client. Legacy per-workspace links still exchange
    // at /collaborators/:clientId/auth.
    path: '/auth/link',
    element: <LinkLoginRoute />,
  },
  {
    // Google's return from the admin sign-in redirect (spec 2026-10-15).
    path: '/auth/google',
    element: <AdminGoogleCallbackRoute />,
  },
  {
    // Legacy alias: old creator-login links redirect to the unified login.
    path: '/creator/login',
    element: <PortalLoginAlias />,
  },
  {
    // Legacy `/client/login` path now routes to the magic-link request page.
    path: '/client/login',
    element: <ClientMagicLoginRoute />,
  },
  {
    // Legacy catch-all - redirect to dashboard
    path: '/*',
    element: <Navigate to="/" replace />,
  },
]);
